import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getAccess } from 'utils';
import DOMPurify from 'dompurify';
import moment from 'moment';
import CreatedInfo from '@/elements/created-info';
import TooltipMenu from '@/elements/TooltipMenu';
import {
  EditIcon,
  ForwardIcon,
  ImportIcon,
  MaximizeIcon,
  TrashIcon,
  CalendarTickIcon,
  LayerIcon,
  LocationIcon,
  DurationIcon,
  BanIcon,
  KeyIcon,
  CategoryIcon,
} from 'assets/icon';
import AnalyticIcon from 'assets/icon/dynamic/AnalyticIcon';
import CardInfo from '@/elements/card-info';
import CardTitle from '@/elements/card-title';
import { Tag } from 'antd';
import { EyeOutlined, DesktopOutlined, CalendarOutlined } from '@ant-design/icons';
import { imgUrl } from 'utils/url';
import './style.css';
import defaultLogo from '../../assets/default-logo.png';
import { ReactComponent as PoiIcon } from '../../assets/poi.svg';
import { ReactComponent as PeopleIcon } from '../../assets/people.svg';

const Cards = ({
  data,
  type,
  isShowMedia,
  editAction,
  deleteAction,
  previewAction,
  previewTemplateAction,
  keyAction,
  downloadAction,
  cardClickAction,
  tagged,
  taggedColor = 'green',
  isPreview = false,
  isDownload = false,
  isClickable = false,
  isEdit = true,
  isDelete = true,
  toAnalytic = false,
  source,
}) => {
  const { siteId } = useParams();
  const [access, setAccess] = useState(null);

  const { userData, role, mediaList } = useSelector(
    (state) => ({
      userData: state.auth.userData,
      role: state.auth.userData.role,
      shapeLookUp: state.editors.shapes,
      mediaList: state.media?.list || [],
    }),
    shallowEqual
  );

  useEffect(() => {
    if (userData && userData.roleAccess) {
      setAccess(getAccess(userData, type, siteId));
    }
  }, [userData, type, siteId]);

  const mediaDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      <CardInfo
        icon={<ForwardIcon style={{ width: 16, height: 16 }} />}
        title={
          data.categoryName &&
          (data.categoryName.length > 20
            ? `${data.categoryName.substr(0, 20)}...`
            : data.categoryName)
        }
      />
      <CardInfo
        icon={<CalendarTickIcon style={{ width: 16, height: 16 }} />}
        title={(() => {
          if (data.schedulingType === 'list') {
            return Array.isArray(data.schedulingIds)
              ? data.schedulingIds.map((schedule) => schedule.name).join(', ')
              : '';
          } else {
            if (data.type === 'date' && data.start && data.end) {
              return `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`;
            }
            if (data.type === 'day' && data.days) {
              return `${data.days[0].day} - ${
                data.days[data.days.length - 1].day
              }`;
            }
            if (data.start && data.end) {
              return `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`;
            }

            return 'All Day';
          }
        })()}
      />
      {data.fileType && (
        <CardInfo
          icon={<DurationIcon style={{ width: 16, height: 16 }} />}
          title={
            data.fileType.split('/')[0] === 'video'
              ? 'Video Duration'
              : data.duration && `${data.duration} seconds`
          }
        />
      )}
    </div>
  );

  const tenantDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />

      <CardInfo
        icon={<LayerIcon style={{ width: 16, height: 16 }} />}
        title={(() => {
          // If POI has multiple floors, show all floor names separated by comma
          let floorDisplay = '';
          if (
            data.floorIds &&
            Array.isArray(data.floorIds) &&
            data.floorIds.length > 0
          ) {
            try {
              const floorIds =
                typeof data.floorIds === 'string'
                  ? JSON.parse(data.floorIds)
                  : data.floorIds;
              const floorNames =
                typeof data.floorNames === 'string'
                  ? JSON.parse(data.floorNames)
                  : data.floorNames;
              if (floorNames && floorNames.length > 0) {
                floorDisplay = floorNames.join(', ');
              } else {
                floorDisplay = data.floorName;
              }
            } catch (e) {
              floorDisplay = data.floorName;
            }
          } else {
            floorDisplay = data.floorName;
          }
          return floorDisplay && floorDisplay.length > 20
            ? `${floorDisplay.substr(0, 20)}...`
            : floorDisplay;
        })()}
      />
      <CardInfo
        icon={<LocationIcon style={{ width: 16, height: 16 }} />}
        title={
          data.location && data.location.length > 20
            ? `${data.location.substr(0, 20)}...`
            : data.location
        }
      />
      {tagged && <Tag color={taggedColor}>{tagged}</Tag>}
    </div>
  );

  const roomDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />

      <CardInfo
        icon={<PoiIcon />}
        title={
          data.poiIds && data.poiIds.length > 0
            ? data.poiIds.map((poi) => poi.name).join(', ')
            : 'No POI assigned'
        }
      />
      <CardInfo icon={<PeopleIcon />} title={`${data.capacity} People`} />
    </div>
  );

  const HtmlView = ({ htmlString }) => {
    const sanitizedHtmlString = DOMPurify.sanitize(htmlString);
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedHtmlString }}
        style={{ height: '50px', overflow: 'hidden' }}
      />
    );
  };

  const descDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      {type === 'screen' && role === 'superadmin' && data.apiKeyPreview && (
        <Tag color={'green'}>API Key: {data.apiKeyPreview}</Tag>
      )}
      {data.enableHtmlView ? (
        <HtmlView htmlString={data.description} />
      ) : (
        <p className="card-list-subtitle">{data.description}</p>
      )}
    </div>
  );

  const contentDetails = (
    <div className="card-list-details">
      <CardTitle title={data.title} />
      <CardInfo
        icon={<CategoryIcon style={{ width: 16, height: 16 }} />}
        title={data.category}
      />
      <CardInfo title={data.content} />
    </div>
  );

  const webPagesDetails = (
    <div className="card-list-details">
      <CardTitle title={data.title} />
      <CardInfo title={data.url} />
    </div>
  );

  const dynamicDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      {data.categoryName && (
        <CardInfo
          icon={<ForwardIcon style={{ width: 16, height: 16 }} />}
          title={
            data.categoryName.length > 20
              ? `${data.categoryName.substr(0, 20)}...`
              : data.categoryName
          }
        />
      )}
      <CardInfo
        icon={<CalendarTickIcon style={{ width: 16, height: 16 }} />}
        title={
          data.start && data.end
            ? `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`
            : 'All Day'
        }
      />
    </div>
  );

  const productDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      <CardInfo title={data.desc} />
    </div>
  );

  const formatTemplateTimestamps = (d) => {
    if (d.start && d.end) {
      return `${moment.unix(d.start).format('DD MMM YYYY')} - ${moment
        .unix(d.end)
        .format('DD MMM YYYY')}`;
    }
    return 'No schedule';
  };

  const templateDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      <p className="card-list-subtitle">{data.description}</p>
      <div className="screen-list" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <DesktopOutlined style={{ color: '#64D5D2' }} />
        <p className="screen-names" style={{ margin: 0, fontSize: 12, color: '#666' }}>
          {data.assignedScreens && data.assignedScreens.length > 0
            ? data.assignedScreens.map((screen, index) => (
                <span key={screen.name || screen._id || index}>
                  {screen.name}
                  {index < data.assignedScreens.length - 1 && ' · '}
                </span>
              ))
            : 'No screens'}
        </p>
      </div>
      <div className="published-date" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <CalendarOutlined style={{ color: '#64D5D2' }} />
        <p style={{ margin: 0, fontSize: 12, color: '#666' }}>{formatTemplateTimestamps(data)}</p>
      </div>
    </div>
  );

  const showByType = () => {
    switch (type) {
      case 'media':
        return mediaDetails;
      case 'dynamic':
        return dynamicDetails;
      case 'tenant':
        return tenantDetails;
      case 'room':
        return roomDetails;
      case 'content':
        return contentDetails;
      case 'web-pages':
        return webPagesDetails;
      case 'web-menu':
        return webPagesDetails;
      case 'web-layout':
        return webPagesDetails;
      case 'product':
        return productDetails;
      case 'template':
        return templateDetails;
      default:
        return descDetails;
    }
  };

  const getFirstMediaFromTemplate = () => {
    if (!data.slots || !Array.isArray(data.slots)) return null;
    for (const slot of data.slots) {
      const arr = slot.medias || slot.allMedia;
      if (arr && Array.isArray(arr) && arr.length > 0) {
        const first = arr[0];
        if (typeof first === 'string') {
          return mediaList.find((x) => x._id === first) || null;
        }
        if (first && first.logo) return first;
        if (first && first._id) {
          return mediaList.find((x) => x._id === first._id) || first;
        }
        return first;
      }
      const ids = slot.mediaIds;
      if (ids && Array.isArray(ids) && ids.length > 0) {
        const id = typeof ids[0] === 'string' ? ids[0] : ids[0]?._id;
        const found = mediaList.find((x) => x._id === id);
        if (found) return found;
      }
    }
    return null;
  };

  const templateMedia = () => {
    const m = getFirstMediaFromTemplate();
    if (!m) {
      return (
        <img src={defaultLogo} alt="default" className="card-list-img" draggable={false} />
      );
    }
    const src = m.logo
      ? `${imgUrl}/media/${m.logo}`
      : m.mediaUrl || defaultLogo;
    const kind = m.fileType ? m.fileType.split('/')[0] : null;
    if (kind === 'video') {
      return (
        <video className="video-js card-list-video" preload="metadata" muted>
          <source src={src} type="video/mp4" />
        </video>
      );
    }
    return (
      <img src={src} alt="template media" className="card-list-img" draggable={false} />
    );
  };

  const renderMedia = () => {
    if (data.logo && data.fileType) {
      const fileType = data.fileType.split('/')[0];
      if (fileType === 'image') {
        return (
          <img
            src={`${imgUrl}/media/${data.logo}`}
            alt="default"
            className="card-list-img"
            draggable={false}
          />
        );
      }
      if (fileType === 'video') {
        return (
          <video
            id="my-video"
            className="video-js card-list-video"
            data-setup="{}"
            preload="metadata"
          >
            <source src={`${imgUrl}/media/${data.logo}`} type="video/mp4" />
            <track kind="captions" />
          </video>
        );
      }
    }
    return null;
  };

  const renderDynamicMedia = () => {
    if (data.logo && data.fileType) {
      const fileType = data.fileType.split('/')[0];
      if (fileType === 'image') {
        return (
          <img
            src={data.logo}
            alt="default"
            className="card-list-img"
            draggable={false}
          />
        );
      }
      if (fileType === 'video') {
        return (
          <video
            id="my-video"
            className="video-js card-list-video"
            data-setup="{}"
            preload="metadata"
          >
            <source src={data.logo} type="video/mp4" />
            <track kind="captions" />
          </video>
        );
      }
    }
    return null;
  };

  const media = <>{renderMedia()}</>;

  const pois = (
    <img
      src={data.logo ? `${imgUrl}/poi/${data.logo}` : defaultLogo}
      alt="default"
      className="card-list-img"
      draggable={false}
    />
  );

  const roomImage = (
    <img
      src={data.image || defaultLogo}
      alt={data.name}
      className="card-list-img"
      draggable={false}
    />
  );

  return (
    <>
      <div
        className={`card-list-container ${
          source === 'placewise-api' ? 'placewise-api-card' : ''
        }`}
        onClick={isClickable ? cardClickAction : () => {}}
      >
        {type === 'tenant' && isShowMedia ? pois : null}
        {type === 'media' && isShowMedia ? media : null}
        {type === 'dynamic' && isShowMedia ? <>{renderDynamicMedia()}</> : null}
        {type === 'room' && isShowMedia ? roomImage : null}
        {type === 'template' && isShowMedia ? templateMedia() : null}
        {showByType()}
      </div>
      <div
        className={`card-list-footer ${
          source === 'placewise-api' ? 'placewise-api-footer' : ''
        }`}
      >
        <div className="card-list-footer-left">
          {source === 'placewise-api' ? (
            <div className="card-list-footer-column">
              <div className="card-list-modified-info">
                <p>Last modified by:</p>
                <p>{data.modifiedBy || 'Unknown'}</p>
              </div>
              <div className="card-list-modified-info">
                <p>Last modified at:</p>
                <p>
                  {data.modifiedAt
                    ? moment(data.modifiedAt).format('DD/MM/YYYY HH:mm')
                    : 'N/A'}
                </p>
              </div>
            </div>
          ) : (
            <CreatedInfo user={data.modifiedBy} />
          )}
        </div>
        <div
          className="card-list-footer-right"
          onClick={(e) => e.stopPropagation()}
        >
          <ul>
            {isEdit &&
              (role !== 'superadmin' ? (
                (access && access.update) || type === 'room' ? (
                  <li>
                    <TooltipMenu
                      title="Edit"
                      clickAction={editAction}
                      icon={<EditIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                ) : null
              ) : (
                <li>
                  <TooltipMenu
                    title="Edit"
                    clickAction={editAction}
                    icon={<EditIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
            {type === 'template' && previewTemplateAction && (
                <li>
                  <TooltipMenu
                    title="Preview Template"
                    clickAction={previewTemplateAction}
                    icon={<EyeOutlined style={{ width: 18, height: 18, color: '#0851B5' }} />}
                  />
                </li>
            )}
            {isPreview && (
              <li>
                <TooltipMenu
                  title="Preview"
                  clickAction={previewAction}
                  icon={<MaximizeIcon style={{ width: 18, height: 18 }} />}
                />
              </li>
            )}
            {type !== 'screen' && isDownload && (
              <li>
                <TooltipMenu
                  title="Download"
                  clickAction={downloadAction}
                  icon={<ImportIcon style={{ width: 18, height: 18 }} />}
                />
              </li>
            )}
            {type === 'screen' && role !== 'superadmin'
              ? null
              : keyAction && (
                  <li>
                    <TooltipMenu
                      title={
                        data.apiKeyPreview
                          ? 'Revoke API Key'
                          : 'Generate API Key'
                      }
                      clickAction={keyAction}
                      icon={
                        data.apiKeyPreview ? (
                          <BanIcon style={{ width: 18, height: 18 }} />
                        ) : (
                          <KeyIcon style={{ width: 18, height: 18 }} />
                        )
                      }
                    />
                  </li>
                )}

            {type !== 'screen' || role !== 'superadmin'
              ? null
              : isDownload && (
                  <li>
                    <TooltipMenu
                      title="Download"
                      clickAction={downloadAction}
                      icon={<ImportIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                )}
            {isDelete &&
              (role !== 'superadmin' ? (
                (access && access.delete) || type === 'room' ? (
                  <li>
                    <TooltipMenu
                      title="Delete"
                      clickAction={deleteAction}
                      icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                ) : null
              ) : (
                <li>
                  <TooltipMenu
                    title="Delete"
                    clickAction={deleteAction}
                    icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
            {toAnalytic &&
              (role !== 'superadmin' ? (
                access &&
                access.delete && (
                  <li>
                    <TooltipMenu
                      title="Analytic"
                      clickAction={toAnalytic}
                      icon={<AnalyticIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                )
              ) : (
                <li>
                  <TooltipMenu
                    title="Analytic"
                    clickAction={toAnalytic}
                    icon={<AnalyticIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Cards;
